import React, { useEffect, useRef } from 'react';

// ShipVideo — pre-rendered holographic burning-container-ship MP4 anchored at
// the RIGHT edge of the deck. Composited with `mix-blend-mode: screen` so the
// dark background of the source video drops out cleanly and only the bright
// hologram lines / flames bleed onto the deck — same treatment as SkullVideo.
//
// We keep the original 3D R3F HologramShip code intact in `HologramShip.jsx`
// so the user can revert with a single line swap in HologramStage.jsx.
export default function ShipVideo({ progressRef, active }) {
  const videoRef = useRef(null);

  // Force-load + best-effort autoplay on mount. Mirrors SkullVideo so the
  // element pulls frames even when its parent wrapper is at opacity 0.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    try { v.load(); } catch (_) {}
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    tryPlay();
    const onMeta = () => tryPlay();
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('canplay', onMeta);
    const unlock = () => tryPlay();
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('touchstart', unlock, { once: true });
    document.addEventListener('scroll', unlock, { once: true, passive: true });
    return () => {
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('canplay', onMeta);
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('scroll', unlock);
    };
  }, []);

  // When the stage flips to 'ship', re-trigger load/play to defeat Chrome's
  // hidden-media optimisation.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (active) {
      try { v.load(); } catch (_) {}
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  }, [active]);

  // Scroll-driven playback rate: faster as we descend through the ship arc.
  useEffect(() => {
    let raf;
    const tick = () => {
      const v = videoRef.current;
      if (v && progressRef && progressRef.current != null) {
        const p = progressRef.current;
        // Ship is alive on deck across 0.05 → 0.72
        const localP = Math.max(0, Math.min(1, (p - 0.05) / 0.67));
        const rate = 0.8 + localP * 0.7; // 0.80 → 1.50
        if (Math.abs(v.playbackRate - rate) > 0.02) v.playbackRate = rate;
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => raf && cancelAnimationFrame(raf);
  }, [progressRef]);

  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 flex items-center justify-center"
      style={{ pointerEvents: 'none' }}
    >
      <video
        ref={videoRef}
        data-testid="ship-hologram-video"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        style={{
          // Full-screen centered (same framing as SkullVideo) so the ship
          // hologram plays at full scale across the deck instead of being
          // cropped to the right half.
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 'min(95vw, 1400px)',
          height: 'min(95vh, 900px)',
          transform: 'translate(-50%, -50%)',
          objectFit: 'contain',
          // Screen blending → bright pixels punch through, near-black drops out.
          mixBlendMode: 'screen',
          // Crush the blacks so the video's dark teal background fully
          // disappears, plus saturate to keep the cyan/amber holo lines hot.
          filter: 'contrast(1.5) brightness(0.95) saturate(1.2)',
          // Soft elliptical mask to feather the rectangular edges.
          WebkitMaskImage:
            'radial-gradient(ellipse 65% 72% at 50% 50%, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)',
          maskImage:
            'radial-gradient(ellipse 65% 72% at 50% 50%, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 100%)',
        }}
      >
        <source src="/assets/video/ship-holo.webm" type="video/webm" />
        <source src="/assets/video/ship-holo.mp4" type="video/mp4" />
      </video>
    </div>
  );
}
